import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    mediaItemId: String,
    favouriteFolderId: String
  }

  connect() {
    console.log("Favourites viewer trigger controller connected")
  }

  async open(event) {
    event.preventDefault()
    
    try {
      // Fetch all media items in the favourite folder
      const currentUserId = document.body.dataset.currentUserId
      const response = await fetch(`/users/${currentUserId}/favourites/${this.favouriteFolderIdValue}/media_items.json`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        console.error('Failed to fetch favourite folder media items:', response.status, response.statusText)
        throw new Error('Failed to fetch favourite folder media items')
      }
      
      const data = await response.json()
      console.log('Fetched favourite folder media items:', data)
      
      // Use all media items (both images and videos)
      const mediaItems = data.media_items
      const currentIndex = mediaItems.findIndex(item => item.id.toString() === this.mediaItemIdValue)
      
      if (currentIndex === -1) {
        console.error('Media item not found:', this.mediaItemIdValue)
        throw new Error('Media item not found')
      }
      
      console.log('Opening favourites viewer for item:', mediaItems[currentIndex])
      this.createModal(mediaItems, currentIndex)
    } catch (error) {
      console.error('Error opening favourites viewer:', error)
      alert('Unable to open favourites viewer. Please try again.')
    }
  }

  createModal(mediaItems, currentIndex) {
    // Create modal structure with its own controller
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black bg-opacity-90 z-50'
    modal.setAttribute('data-controller', 'favourites-viewer-modal')
    modal.setAttribute('data-favourites-viewer-modal-media-items-value', JSON.stringify(mediaItems))
    modal.setAttribute('data-favourites-viewer-modal-current-index-value', currentIndex)
    modal.setAttribute('data-favourites-viewer-modal-favourite-folder-id-value', this.favouriteFolderIdValue)
    
    modal.innerHTML = `
      <div class="relative w-full h-full flex items-center justify-center">
        <!-- Close button -->
        <button 
          class="absolute top-4 left-4 bg-red-500 hover:bg-red-600 text-black font-medium px-2 py-1 sm:px-4 sm:py-2 rounded-lg z-50 flex items-center gap-1 sm:gap-2 transition-colors duration-200 text-sm sm:text-base" 
          data-action="click->favourites-viewer-modal#close" 
          type="button">
          <span class="hidden sm:inline">Close</span>
          <span class="sm:hidden">X</span>
          <svg class="hidden sm:block w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Remove from Favourite Folder button -->
        <div class="absolute top-4 right-4 z-50">
          <button 
            class="bg-red-600 hover:bg-red-700 text-white font-medium px-2 py-1 sm:px-4 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors duration-200 text-sm sm:text-base" 
            data-action="click->favourites-viewer-modal#removeCurrentItem"
            type="button">
            <span class="hidden sm:inline">Remove from Folder</span>
            <span class="sm:hidden">Remove</span>
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>

        <!-- Navigation buttons -->
        <div class="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
          <button class="text-white hover:text-gray-300 focus:outline-none select-none" data-action="click->favourites-viewer-modal#showPrevious" type="button">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        <div class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10">
          <button class="text-white hover:text-gray-300 focus:outline-none select-none" data-action="click->favourites-viewer-modal#showNext" type="button">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <!-- Media container -->
        <div class="relative max-w-full max-h-full p-4" data-favourites-viewer-modal-target="mediaContainer">
          <!-- Image element (for images) -->
          <img class="max-h-[90vh] max-w-full object-contain transition-opacity duration-200" src="" alt="" style="display: none;" />
          <!-- Video element (for videos) -->
          <video controls autoplay loop muted class="max-h-[90vh] max-w-full" style="display: none;">
            <source src="" type="">
            Your browser does not support the video tag.
          </video>
          <div class="absolute bottom-0 left-0 right-0 text-center text-white text-sm py-2">
            <span data-favourites-viewer-modal-target="currentIndex"></span> / <span data-favourites-viewer-modal-target="totalCount"></span>
          </div>
        </div>
      </div>
    `

    // Add to document
    document.body.appendChild(modal)
    
    // Let Stimulus automatically detect and connect to the new controller
    // The modal will be processed on the next tick
  }
} 